import axios from 'axios';
import { defineComponent } from 'vue';
import Datepicker from 'vanillajs-datepicker/Datepicker';
import constants from '../shared/constants';
import {
    formatDate,
    getInnerFormElement as getInnerHtmlFormElement,
    initializeDatepicker,
    initializeTurnstile,
    scrollToElement as scrollToDomElement,
    scrollToField as scrollToInvalidField
} from '../shared/functions';
import type { ChangeDateEvent } from '../shared/interfaces';
import type {
    VacancyDatepickerInputElement,
    VacancyFormInput,
    VacancyFormOrganizationType,
    VacancyFormOption,
    VacancyFormStep,
    VacancyFormStepContent
} from '../shared/types';

export default defineComponent({
    data() {
        return {
            datepickerInstances: {} as Record<string, Datepicker>,
            form: [
                { index: 1, active: true, heading: '', inputs: [] },
                { index: 2, active: false, heading: '', inputs: [] },
                { index: 3, active: false, heading: '', inputs: [] },
                { index: 4, active: false, heading: '', inputs: [] },
            ] as VacancyFormStep[],
            honeypot: '',
            organizationTypes: [] as VacancyFormOrganizationType[],
            pictureUploadPath: '',
            regions: [] as VacancyFormOption[],
            stepContent: [] as VacancyFormStepContent[],
            turnstileCompleted: false,
            vacancyTypes: [] as VacancyFormOption[],
        };
    },
    computed: {
        currentStep(): number {
            return this.form.find((step) => step.active)?.index ?? 1;
        },
        currentIntro(): VacancyFormStepContent {
            return this.stepContent[this.currentStep - 1] ?? { title: '', body: '' };
        },
        nextStepHeading(): string {
            return this.getStep(this.currentStep + 1)?.heading ?? '';
        },
    },
    mounted() {
        this.readDataset();
        this.setFormData();
        this.$nextTick(() => {
            this.initializeDatepickers();
            this.initializeTurnstile();
        });
    },
    beforeUnmount() {
        this.destroyDatepickers();
    },
    methods: {
        getRootElement(): HTMLElement | null {
            return this.$el as HTMLElement | null;
        },
        getFormElement(): HTMLElement | null {
            return this.getRootElement()?.querySelector('.form') ?? null;
        },
        getInnerFormElement(): HTMLFormElement | null {
            return getInnerHtmlFormElement(this.getFormElement());
        },
        getTurnstileElement(): HTMLElement | null {
            return this.getFormElement()?.querySelector<HTMLElement>(constants.targets.vacancyForm.turnstile) ?? null;
        },
        initializeTurnstile(): void {
            initializeTurnstile(this.getTurnstileElement(), (completed) => {
                this.turnstileCompleted = completed;
            });
        },
        readJsonData<T>(value: string | undefined, fallback: T): T {
            const content = value?.trim();

            if (!content) return fallback;

            try {
                return JSON.parse(content) as T;
            } catch {
                return fallback;
            }
        },
        readDataset(): void {
            const form = this.getFormElement();

            if (!form) return;

            this.organizationTypes = this.readJsonData<VacancyFormOrganizationType[]>(form.dataset.organizationTypes, []);
            this.regions = this.readJsonData<VacancyFormOption[]>(form.dataset.regions, []);
            this.vacancyTypes = this.readJsonData<VacancyFormOption[]>(form.dataset.vacancyTypes, []);
            this.stepContent = this.readJsonData<VacancyFormStepContent[]>(form.dataset.stepContent, []);
            this.pictureUploadPath = form.dataset.pictureUploadPath ?? '';
            this.honeypot = form.dataset.nameText ?? '';
        },
        setFormData(): void {
            const form = this.getFormElement();

            if (!form) return;

            this.getStep(1)!.heading = form.dataset.headingStep1 ?? '';
            this.getStep(1)!.inputs.push({
                label: form.dataset.labelTitle ?? '',
                name: form.dataset.nameTitle ?? '',
                error: form.dataset.errorTitle ?? '',
                type: 'text',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.getStep(1)!.inputs.push({
                label: form.dataset.labelOrgtype ?? '',
                name: form.dataset.nameOrgtype ?? '',
                error: form.dataset.errorOrgtype ?? '',
                placeholder: form.dataset.placeholderOrgtype ?? '',
                type: 'select',
                options: this.organizationTypes.map((organizationType) => ({
                    label: organizationType.name,
                    value: organizationType.id,
                    organizations: organizationType.organizations,
                })),
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.getStep(1)!.inputs.push({
                label: form.dataset.labelOrgname ?? '',
                name: form.dataset.nameOrgname ?? '',
                error: form.dataset.errorOrgname ?? '',
                placeholder: form.dataset.placeholderOrgname ?? '',
                type: 'select',
                options: [],
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.getStep(1)!.inputs.push({
                label: form.dataset.labelNeworg ?? '',
                name: form.dataset.nameNeworg ?? '',
                type: 'checkbox',
                required: false,
                pristine: true,
                valid: true,
                value: false,
            });

            this.getStep(1)!.inputs.push({
                label: form.dataset.labelWorktype ?? '',
                name: form.dataset.nameWorktype ?? '',
                error: form.dataset.errorWorktype ?? '',
                type: 'checkbox-group',
                options: this.vacancyTypes.map((vacancyType) => ({
                    label: vacancyType.label,
                    name: vacancyType.value,
                    value: vacancyType.value,
                    checked: false,
                })),
                required: true,
                pristine: true,
                valid: false,
                value: [],
            });

            this.getStep(1)!.inputs.push({
                label: form.dataset.labelDescription ?? '',
                name: form.dataset.nameDescription ?? '',
                error: form.dataset.errorDescription ?? '',
                help: form.dataset.helpDescription ?? '',
                type: 'textarea',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.getStep(2)!.heading = form.dataset.headingStep2 ?? '';
            this.getStep(2)!.inputs.push({
                label: form.dataset.labelHours ?? '',
                name: form.dataset.nameHours ?? '',
                type: 'text',
                required: false,
                pristine: true,
                valid: true,
                value: '',
            });

            this.getStep(2)!.inputs.push({
                label: form.dataset.labelWorkdays ?? '',
                name: form.dataset.nameWorkdays ?? '',
                type: 'checkbox-group',
                options: [
                    { label: 'Maandag', name: 'maandag', value: 'maandag', checked: false },
                    { label: 'Dinsdag', name: 'dinsdag', value: 'dinsdag', checked: false },
                    { label: 'Woensdag', name: 'woensdag', value: 'woensdag', checked: false },
                    { label: 'Donderdag', name: 'donderdag', value: 'donderdag', checked: false },
                    { label: 'Vrijdag', name: 'vrijdag', value: 'vrijdag', checked: false },
                    { label: 'Zaterdag', name: 'zaterdag', value: 'zaterdag', checked: false },
                    { label: 'Zondag', name: 'zondag', value: 'zondag', checked: false },
                ],
                required: false,
                pristine: true,
                valid: true,
                value: [],
            });

            const minimumDate = new Date();
            const maximumDate = new Date();
            maximumDate.setFullYear(maximumDate.getFullYear() + 1);

            this.getStep(2)!.inputs.push({
                label: form.dataset.labelClosingdate ?? '',
                name: form.dataset.nameClosingdate ?? '',
                error: form.dataset.errorClosingdate ?? '',
                help: form.dataset.helpClosingdate ?? '',
                type: 'date',
                required: true,
                pristine: true,
                valid: false,
                value: '',
                min: [
                    minimumDate.getFullYear(),
                    String(minimumDate.getMonth() + 1).padStart(2, '0'),
                    String(minimumDate.getDate()).padStart(2, '0'),
                ].join('-'),
                max: [
                    maximumDate.getFullYear(),
                    String(maximumDate.getMonth() + 1).padStart(2, '0'),
                    String(maximumDate.getDate()).padStart(2, '0'),
                ].join('-'),
            });

            this.getStep(2)!.inputs.push({
                label: form.dataset.labelPicture ?? '',
                name: form.dataset.namePicture ?? '',
                help: form.dataset.helpPicture ?? '',
                button: form.dataset.buttonPicture ?? '',
                remove: form.dataset.removePicture ?? '',
                type: 'file',
                required: false,
                pristine: true,
                valid: true,
                value: '',
                filename: '',
                src: '',
            });

            this.getStep(2)!.inputs.push({
                label: form.dataset.labelRegions ?? '',
                name: form.dataset.nameRegions ?? '',
                error: form.dataset.errorRegions ?? '',
                type: 'checkbox-group',
                options: this.regions.map((region) => ({
                    label: region.label,
                    name: region.value,
                    value: region.value,
                    checked: false,
                })),
                required: true,
                pristine: true,
                valid: false,
                value: [],
            });

            this.getStep(3)!.heading = form.dataset.headingStep3 ?? '';
            this.getStep(3)!.inputs.push({
                label: form.dataset.labelFirst ?? '',
                name: form.dataset.nameFirst ?? '',
                error: form.dataset.errorFirst ?? '',
                type: 'text',
                required: true,
                pristine: true,
                valid: false,
                value: '',
                group: true,
            });

            this.getStep(3)!.inputs.push({
                label: form.dataset.labelInsertion ?? '',
                name: form.dataset.nameInsertion ?? '',
                short: true,
                type: 'text',
                required: false,
                pristine: true,
                valid: true,
                value: '',
                group: true,
            });

            this.getStep(3)!.inputs.push({
                label: form.dataset.labelLast ?? '',
                name: form.dataset.nameLast ?? '',
                error: form.dataset.errorLast ?? '',
                type: 'text',
                required: true,
                pristine: true,
                valid: false,
                value: '',
                group: true,
            });

            this.getStep(3)!.inputs.push({
                label: form.dataset.labelEmail ?? '',
                name: form.dataset.nameEmail ?? '',
                error: form.dataset.errorEmail ?? '',
                type: 'email',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.getStep(3)!.inputs.push({
                label: form.dataset.labelPhone ?? '',
                name: form.dataset.namePhone ?? '',
                error: form.dataset.errorPhone ?? '',
                type: 'tel',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.getStep(3)!.inputs.push({
                label: form.dataset.labelWebsite ?? '',
                name: form.dataset.nameWebsite ?? '',
                error: form.dataset.errorWebsite ?? '',
                help: form.dataset.helpWebsite ?? '',
                type: 'url',
                required: false,
                pristine: true,
                valid: true,
                value: '',
            });

            this.getStep(4)!.heading = form.dataset.headingStep4 ?? '';
        },
        getStep(index: number): VacancyFormStep | undefined {
            return this.form.find((step) => step.index === index);
        },
        getInput(index: number, name: string): VacancyFormInput | undefined {
            return this.getStep(index)?.inputs.find((input) => input.name === name);
        },
        getOptionId(inputName: string, optionValue: string, index: number): string {
            const sanitizedValue = optionValue.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-');

            return `${inputName}-${index}-${sanitizedValue}`;
        },
        scrollToElement(element: HTMLElement | null): void {
            scrollToDomElement(element);
        },
        scrollToIntro(): void {
            requestAnimationFrame(() => {
                this.scrollToElement(
                    this.getRootElement()?.querySelector<HTMLElement>(constants.targets.vacancyForm.intro) ?? null
                );
            });
        },
        getDatepickerInputId(input: VacancyFormInput): string {
            return `${input.name}_1`;
        },
        getDateInputDisplayValue(input: VacancyFormInput): string {
            const comparableValue = this.getComparableDateValue(typeof input.value === 'string' ? input.value : '');

            if (!comparableValue) return '';

            const [year, month, day] = comparableValue.split('-');

            if (!year || !month || !day) return '';

            return `${day}-${month}-${year}`;
        },
        getComparableDateValue(value: string): string {
            if (!value) return '';

            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return value;
            }

            const date = new Date(value);

            if (Number.isNaN(date.getTime())) return '';

            return [
                date.getFullYear(),
                String(date.getMonth() + 1).padStart(2, '0'),
                String(date.getDate()).padStart(2, '0'),
            ].join('-');
        },
        initializeDatepickers(): void {
            const activeStep = this.getStep(this.currentStep);

            if (!activeStep) return;

            activeStep.inputs
                .filter((input) => input.type === 'date')
                .forEach((input) => {
                    const inputId = this.getDatepickerInputId(input);
                    const datepickerElement = document.getElementById(inputId) as VacancyDatepickerInputElement | null;

                    if (!datepickerElement) return;

                    if (datepickerElement._vacancyFormDatepickerChangeHandler) {
                        datepickerElement.removeEventListener('changeDate', datepickerElement._vacancyFormDatepickerChangeHandler);
                    }

                    const datepicker = initializeDatepicker(datepickerElement, {
                        minDate: input.min ? new Date(`${input.min}T12:00:00`) : undefined,
                        maxDate: input.max ? new Date(`${input.max}T12:00:00`) : undefined,
                    });
                    datepickerElement._vacancyFormDatepickerChangeHandler = (event: Event) => {
                        const changeDateEvent = event as ChangeDateEvent;
                        const date = new Date(new Date(changeDateEvent.detail.date).setHours(12, 0, 0, 0));

                        input.value = date.toISOString();
                        input.pristine = false;
                        this.validateInput(input);
                    };
                    datepickerElement.addEventListener('changeDate', datepickerElement._vacancyFormDatepickerChangeHandler);

                    this.datepickerInstances[inputId] = datepicker;
                });
        },
        destroyDatepickers(): void {
            Object.values(this.datepickerInstances).forEach((datepicker) => {
                datepicker.destroy();
            });

            this.form.forEach((step) => {
                step.inputs
                    .filter((input) => input.type === 'date')
                    .forEach((input) => {
                        const datepickerElement = document.getElementById(this.getDatepickerInputId(input)) as VacancyDatepickerInputElement | null;

                        if (!datepickerElement?._vacancyFormDatepickerChangeHandler) return;

                        datepickerElement.removeEventListener('changeDate', datepickerElement._vacancyFormDatepickerChangeHandler);
                        delete datepickerElement._vacancyFormDatepickerChangeHandler;
                    });
            });

            this.datepickerInstances = {};
        },
        setStep(index: number): void {
            const currentStep = this.form.find((step) => step.active);
            const nextStep = this.getStep(index);

            if (!nextStep) return;

            if (index !== 4) this.turnstileCompleted = false;

            if (currentStep) currentStep.active = false;
            nextStep.active = true;

            this.$nextTick(() => {
                this.initializeDatepickers();
                this.initializeTurnstile();
                this.scrollToIntro();
            });
        },
        setOrganizations(): void {
            const form = this.getFormElement();
            const organizationTypeInput = this.getInput(1, form?.dataset.nameOrgtype ?? '');
            const organizationNameInput = this.getInput(1, form?.dataset.nameOrgname ?? '');
            const selectedOrganizationType = organizationTypeInput?.options?.find(
                (option) => option.value === organizationTypeInput.value
            );
            const currentOrganizationValue = typeof organizationNameInput?.value === 'string'
                ? organizationNameInput.value
                : '';

            if (!organizationTypeInput || !organizationNameInput) return;

            const nextOptions = (selectedOrganizationType?.organizations ?? []).map((organization) => ({
                label: organization.name,
                value: organization.id,
            }));
            const hasCurrentOrganizationValue = nextOptions.some(
                (option) => option.value === currentOrganizationValue
            );

            organizationNameInput.options = nextOptions;

            if (organizationNameInput.type !== 'select') return;

            if (hasCurrentOrganizationValue) {
                organizationNameInput.valid = true;
                return;
            }

            organizationNameInput.value = '';
            organizationNameInput.valid = false;
            organizationNameInput.pristine = true;
        },
        setNewOrganization(input: VacancyFormInput): void {
            const form = this.getFormElement();
            const organizationNameInput = this.getInput(1, form?.dataset.nameOrgname ?? '');

            if (!form || !organizationNameInput) return;

            organizationNameInput.label = input.value
                ? form.dataset.labelNeworgname ?? ''
                : form.dataset.labelOrgname ?? '';
            organizationNameInput.type = input.value ? 'text' : 'select';
            organizationNameInput.value = '';
            organizationNameInput.valid = false;
            organizationNameInput.pristine = true;
        },
        setCheckboxGroup(input: VacancyFormInput): void {
            input.pristine = false;
            input.value = (input.options ?? [])
                .filter((option) => option.checked)
                .map((option) => option.name ?? option.value);

            this.validateInput(input);
        },
        validateInput(input: VacancyFormInput): void {
            const stringValue = typeof input.value === 'string' ? input.value : '';

            switch (input.type) {
                case 'text':
                    if (!input.required && stringValue.length === 0) {
                        input.valid = true;
                        return;
                    }

                    input.valid = stringValue.trim().length > 0;

                    if (this.getFormElement()?.dataset.nameTitle === input.name) {
                        input.valid = input.valid && stringValue.length <= 50;
                    }

                    return;
                case 'email': {
                    const sanitized = stringValue.replace(/\s+/g, '').trim();
                    const emailRegex = /\S+@\S+\.\S+/;

                    input.value = sanitized;
                    input.valid = emailRegex.test(sanitized);
                    return;
                }
                case 'url':
                    if (!stringValue) {
                        input.valid = true;
                        return;
                    }

                    input.value = stringValue.replace(/\s+/g, '').trim();
                    input.valid = /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i
                        .test(String(input.value));
                    return;
                case 'tel': {
                    const sanitized = stringValue.replace(/\D/g, '').trim();

                    input.value = sanitized;
                    input.valid = sanitized.length === 10;
                    return;
                }
                case 'textarea':
                    if (!input.required && stringValue.length === 0) {
                        input.valid = true;
                        return;
                    }

                    input.valid = stringValue.trim().length > 0 && stringValue.length <= 3800;
                    return;
                case 'date':
                    if (!stringValue) {
                        input.valid = !input.required;
                        return;
                    }

                    {
                        const comparableValue = this.getComparableDateValue(stringValue);

                        input.valid = Boolean(
                            comparableValue
                            && input.min
                            && input.max
                            && comparableValue >= input.min
                            && comparableValue <= input.max
                        );
                    }
                    return;
                case 'checkbox-group':
                    input.valid = Array.isArray(input.value) && input.value.length > 0;
                    return;
                case 'select':
                    input.valid = Boolean(input.value);

                    if (this.getFormElement()?.dataset.nameOrgtype === input.name) {
                        this.setOrganizations();
                    }

                    return;
                case 'checkbox':
                    input.valid = Boolean(input.value);
                    return;
                case 'file':
                    input.valid = !input.required || Boolean(input.src);
                    return;
                default:
                    input.valid = !input.required || Boolean(input.value);
                    return;
            }
        },
        hasValidationError(input: VacancyFormInput): boolean {
            if (input.type === 'url') {
                return !input.valid;
            }

            return input.required && !input.valid;
        },
        validateFile(input: VacancyFormInput, event: Event): void {
            const target = event.target as HTMLInputElement | null;
            const file = target?.files?.[0];

            input.pristine = false;

            if (!target || !file) {
                this.resetFile(input);
                return;
            }

            input.value = file.name;
            input.filename = file.name;
            input.valid = true;

            const fileReader = new FileReader();
            fileReader.onload = (loadEvent) => {
                input.src = String(loadEvent.target?.result ?? '');
            };
            fileReader.readAsDataURL(file);
        },
        resetFile(input: VacancyFormInput): void {
            const fileInputElement = document.getElementById(input.name) as HTMLInputElement | null;

            if (fileInputElement) {
                fileInputElement.value = '';
            }

            input.value = '';
            input.filename = '';
            input.src = '';
            input.valid = true;
        },
        validateStep(index: number): void {
            const step = this.getStep(index);

            if (!step) return;

            step.inputs.forEach((input) => {
                input.pristine = false;
                this.validateInput(input);
            });

            const firstInvalidInput = step.inputs.find((input) => this.hasValidationError(input));

            if (!firstInvalidInput) {
                this.setStep(index + 1);
                return;
            }

            scrollToInvalidField(`${firstInvalidInput.name}Field`);
        },
        formatDisplayDate(value: string): string {
            if (!value) return '';

            const comparableValue = this.getComparableDateValue(value);

            if (!comparableValue) return '';

            const [year, month, day] = comparableValue.split('-');

            if (!year || !month || !day) return '';

            return `${day}-${month}-${year}`;
        },
        getSelectedOptionLabel(input: VacancyFormInput): string {
            return input.options?.find((option) => option.value === input.value)?.label ?? '';
        },
        getSelectedCheckboxLabels(input: VacancyFormInput): string {
            return (input.options ?? [])
                .filter((option) => option.checked)
                .map((option) => option.label)
                .join(', ');
        },
        hasSummaryValue(input: VacancyFormInput): boolean {
            switch (input.type) {
                case 'file':
                    return Boolean(input.src);
                case 'checkbox':
                    return Boolean(input.value);
                case 'checkbox-group':
                    return Array.isArray(input.value) && input.value.length > 0;
                default:
                    return Boolean(input.value);
            }
        },
        async submitForm(): Promise<void> {
            const form = this.getFormElement();
            const innerForm = this.getInnerFormElement();
            const fileInput = this.getInput(2, form?.dataset.namePicture ?? '');

            if (!innerForm) return;

            if (fileInput?.type !== 'file' || !fileInput.src || !this.pictureUploadPath) {
                innerForm.submit();
                return;
            }

            const formData = new FormData();
            formData.append(
                fileInput.name,
                fileInput.src.replace(/^data:image\/[a-zA-Z0-9.+-]+;base64,/, '')
            );
            formData.append('Filename', fileInput.filename?.split('.').slice(0, -1).join('.') || formatDate(new Date()));

            try {
                const response = await axios.post<string>(this.pictureUploadPath, formData);
                const hiddenInput = document.createElement('input');

                hiddenInput.type = 'hidden';
                hiddenInput.name = fileInput.name;
                hiddenInput.value = response.data;
                hiddenInput.dataset.vacancyPicturePath = 'true';

                innerForm.querySelector(constants.targets.vacancyForm.picturePath)?.remove();
                innerForm.appendChild(hiddenInput);
                innerForm.submit();
            } catch (error) {
                console.error(error);
            }
        },
    },
});
